import { Box, Chip, LinearProgress, Typography } from '@mui/material';
import { useGetItemPatternQuery } from 'api/ml-service/ml-service';
import UtilityDrawerBody from 'components/drawers/utility-drawer/utility-drawer-body/utility-drawer-body.component';
import UtilityDrawer from 'components/drawers/utility-drawer/utility-drawer.component';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import groupby from 'lodash.groupby';
import { PopularItemType } from 'api/ml-service/ml-service.types';
import { skipToken } from '@reduxjs/toolkit/dist/query';

type ItemAnalysisDrawerProps = {
  open: boolean;
  close: () => void;
  drawerData?: {
    itemName: string;
  };
};

const ItemAnalysisDrawer = ({
  open,
  close,
  drawerData,
}: ItemAnalysisDrawerProps) => {
  const { t } = useTranslation();
  const itemName = drawerData?.itemName;
  const { data, isFetching } = useGetItemPatternQuery(itemName ?? skipToken);

  const categoryDetails: Record<string, PopularItemType[]> = useMemo(
    () => groupby(data?.popularItems, 'category'),
    [data?.popularItems],
  );

  return (
    <UtilityDrawer
      open={open}
      onClose={close}
      data-testid="ItemAnalysisDrawer.Drawer"
      title={t('drawer.itemAnalysisDrawer.title', { itemName })}
      subtitle={
        <Typography
          variant="body1"
          data-testid="UtilityDrawer.Typography.Subtitle"
        >
          {t('drawer.itemAnalysisDrawer.subtitle')}
        </Typography>
      }
      isLoading={isFetching}
    >
      <UtilityDrawerBody>
        {!isFetching &&
          Object.entries(categoryDetails)
            .sort(([a], [b]) => {
              const order = [
                'main',
                'side',
                'dessert',
                'beverage',
                'modifier',
                'non food',
              ];
              return order.indexOf(a) - order.indexOf(b);
            })
            .map(([category, items]) => {
              return (
                <div key={category}>
                  <Typography
                    variant="h2"
                    sx={{ mt: 2, textTransform: 'capitalize' }}
                    data-testid="ItemAnalysisDrawer.Typography"
                  >
                    {t('drawer.itemAnalysisDrawer.categories.title', {
                      category,
                    })}
                  </Typography>
                  {items?.map((item, index) => {
                    if (index >= 3) return null;
                    const value = Math.round(item.support * 100);
                    const color = value >= 50 ? 'success' : 'info';

                    return (
                      <Box key={item.itemName} sx={{ mb: 2 }}>
                        <Typography fontWeight="bold">
                          {item.itemName}
                        </Typography>
                        <Box
                          display="grid"
                          gridTemplateColumns="1fr auto"
                          gap={2}
                          alignItems="center"
                        >
                          <LinearProgress
                            variant="determinate"
                            value={value}
                            sx={{ height: 14, borderRadius: 5 }}
                            color={color}
                            data-testid={`ItemAnalysisDrawer.LinearProgress.${item.itemName}`}
                          />
                          <Chip
                            label={`${value}%`}
                            color={color}
                            data-testid={`ItemAnalysisDrawer.Chip.${item.itemName}`}
                          />
                        </Box>
                      </Box>
                    );
                  })}
                </div>
              );
            })}
      </UtilityDrawerBody>
    </UtilityDrawer>
  );
};

export default ItemAnalysisDrawer;
